import { useState } from 'react';
import type { ClarificationQuestion } from '@localagent/sdk';

/** Form submission data from the clarification card. */
interface ClarificationSubmission {
  questionId: string;
  selectedOption?: string | null;
  openResponse?: string | null;
  skipped: boolean;
}

interface ClarificationCardProps {
  question: ClarificationQuestion;
  onSubmit: (response: ClarificationSubmission) => void;
  disabled?: boolean;
}

export function ClarificationCard({ question, onSubmit, disabled }: ClarificationCardProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [openResponse, setOpenResponse] = useState('');

  function handleSubmit() {
    onSubmit({
      questionId: question.questionId,
      selectedOption,
      openResponse: openResponse.trim() || null,
      skipped: false,
    });
  }

  function handleSkip() {
    onSubmit({
      questionId: question.questionId,
      skipped: true,
    });
  }

  const hasSelection = selectedOption != null || openResponse.trim().length > 0;

  return (
    <div className="rounded bg-blue-500/10 p-2.5 dark:bg-blue-950/30">
      {/* Question */}
      <p className="mb-2 text-xs font-medium text-foreground">{question.question}</p>

      {/* Options */}
      {question.options.length > 0 && (
        <div className="mb-2 space-y-1">
          {question.options.map((option) => (
            <button
              key={option.label}
              type="button"
              disabled={disabled}
              className={`flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-[11px] transition-colors ${
                selectedOption === option.label
                  ? 'bg-blue-100 dark:bg-blue-900/40'
                  : 'bg-card hover:bg-muted'
              } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              onClick={() => setSelectedOption(option.label)}
            >
              {/* Radio circle */}
              <div className={`mt-0.5 flex h-3 w-3 shrink-0 items-center justify-center rounded-full border ${
                selectedOption === option.label
                  ? 'border-blue-500 bg-blue-500'
                  : 'border-muted-foreground/40'
              }`}>
                {selectedOption === option.label && (
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                )}
              </div>
              <div>
                <span className="font-medium text-foreground">{option.label}</span>
                {option.description && (
                  <p className="mt-0.5 text-[10px] text-muted-foreground">{option.description}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Open response text input */}
      <div className="mb-2">
        <textarea
          className="w-full rounded bg-background px-2 py-1.5 text-[11px] dark:bg-muted text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="Or type a response..."
          rows={2}
          value={openResponse}
          onChange={(e) => setOpenResponse(e.target.value)}
          disabled={disabled}
        />
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || !hasSelection}
          className="rounded bg-blue-500 px-3 py-1 text-[11px] font-medium text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleSubmit}
        >
          Submit
        </button>
        <button
          type="button"
          disabled={disabled}
          className="rounded px-3 py-1 text-[11px] text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleSkip}
        >
          Skip
        </button>
      </div>
    </div>
  );
}
